import React, { useState, useEffect } from 'react';
import { X, Clock, CheckCircle, XCircle } from 'lucide-react';

const AutomaticAllocationModal = ({
  isOpen,
  onClose,
  bookingData,
  selectedAmbulance,
  availableDrivers = [],
  onAllocationComplete,
  onAllocationFailed
}) => {
  const [currentDriverIndex, setCurrentDriverIndex] = useState(0);
  const [countdown, setCountdown] = useState(15);
  const [isAllocating, setIsAllocating] = useState(false);
  const [allocationComplete, setAllocationComplete] = useState(false);
  const [allocatedDriver, setAllocatedDriver] = useState(null);

  // Start allocation process
  useEffect(() => {
    console.log('🔄 AutomaticAllocationModal useEffect:', { isOpen, availableDriversLength: availableDrivers.length });
    if (isOpen && availableDrivers.length > 0) {
      // Reset state when modal opens
      console.log('🔄 Starting allocation process');
      setCurrentDriverIndex(0);
      setAllocationComplete(false);
      setAllocatedDriver(null);
      setIsAllocating(true);
      setCountdown(15);
    } else if (isOpen && availableDrivers.length === 0) {
      console.log('⚠️ Modal is open but no drivers available');
    }
  }, [isOpen, availableDrivers]);

  // Countdown timer
  useEffect(() => {
    let timer;
    if (isAllocating && countdown > 0 && !allocationComplete) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0 && !allocationComplete) {
      // Timeout - move to next driver or fail
      moveToNextDriver();
    }
    return () => clearTimeout(timer);
  }, [countdown, isAllocating, allocationComplete]);

  const handleDriverAccept = (driver) => {
    console.log(`✅ Driver ${driver.name} accepted the request`);
    setAllocationComplete(true);
    setAllocatedDriver(driver);
    setIsAllocating(false);
    
    if (onAllocationComplete) {
      onAllocationComplete({
        driver,
        ambulance: selectedAmbulance,
        bookingData
      });
    }
  };

  const moveToNextDriver = () => {
    const nextIndex = currentDriverIndex + 1;
    if (nextIndex >= availableDrivers.length) {
      // No more drivers
      handleAllocationFailed();
    } else {
      setCurrentDriverIndex(nextIndex);
      setCountdown(15);
    }
  };

  const handleAllocationFailed = () => {
    console.log('❌ Allocation failed - no drivers available');
    setIsAllocating(false);
    setAllocationComplete(true);
    
    if (onAllocationFailed) {
      onAllocationFailed({
        reason: 'No drivers available',
        bookingData
      });
    }
  };

  // Simulate driver accept for demo purposes
  const handleSimulateAccept = () => {
    if (availableDrivers[currentDriverIndex]) {
      handleDriverAccept(availableDrivers[currentDriverIndex]);
    }
  };

  // Get current driver
  const currentDriver = availableDrivers[currentDriverIndex];

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-auto">
        {/* Header with close button */}
        <div className="flex justify-end p-4 pb-0">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-8 pb-8 text-center">
          {/* Clock Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center">
              <Clock className="w-10 h-10 text-yellow-600" />
            </div>
          </div>

          {/* Title and Subtitle */}
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Waiting for Driver Acceptance
          </h2>
          <p className="text-gray-600 mb-8">
            Contacting ambulance driver...
          </p>

          {/* Driver Details */}
          {currentDriver && (
            <div className="space-y-4 mb-8">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Ambulance Number</span>
                <span className="font-semibold text-gray-900">
                  {currentDriver.ambulanceNumber || 'AMB-001'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Driver Name</span>
                <span className="font-semibold text-gray-900">
                  {currentDriver.name}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Driver Number</span>
                <span className="font-semibold text-gray-900">
                  {currentDriver.phone}
                </span>
              </div>
            </div>
          )}

          {/* Countdown Timer */}
          {isAllocating && !allocationComplete && (
            <div className="mb-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-red-600" />
                <span className="text-2xl font-bold text-red-600">
                  {countdown}s
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-red-600 h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${(countdown / 15) * 100}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Success State */}
          {allocationComplete && allocatedDriver && (
            <div className="mb-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <span className="text-lg font-semibold text-green-600">
                  Driver Accepted!
                </span>
              </div>
            </div>
          )}

          {/* Failed State */}
          {allocationComplete && !allocatedDriver && (
            <div className="mb-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <XCircle className="w-6 h-6 text-red-600" />
                <span className="text-lg font-semibold text-red-600">
                  No Drivers Available
                </span>
              </div>
            </div>
          )}

          {/* Demo Button */}
          {isAllocating && !allocationComplete && (
            <button
              onClick={handleSimulateAccept}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold transition-colors"
            >
              Simulate Driver Accept (Demo)
            </button>
          )}

          {/* Close Button for completed states */}
          {allocationComplete && (
            <button
              onClick={onClose}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold transition-colors"
            >
              {allocatedDriver ? 'Continue' : 'Try Again'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutomaticAllocationModal;